import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, of } from 'rxjs';
import { map, catchError, tap, shareReplay, switchMap } from 'rxjs/operators';
import { 
  DocumentationConfig, 
  DocumentationContent, 
  DocumentationError, 
  DocumentationItem, 
  DocumentationSection,
  DocumentationMenuState,
  NavigationContext,
  NavigationLink,
  BreadcrumbItem
} from '../models/documentation.interface';

/**
 * Service for managing API Mesh documentation
 * Loads configuration and Markdown files from public folder
 * Provides navigation and content management functionality
 */
@Injectable({
  providedIn: 'root'
})
export class DocumentationService {
  private readonly baseUrl = '/api-mesh-docs';
  private readonly configUrl = `${this.baseUrl}/api-mesh-docs.json`;
  
  // Cache for loaded configuration
  private configCache$?: Observable<DocumentationConfig>;
  
  // Cache for content
  private contentCache = new Map<string, DocumentationContent>();
  
  // State management for menu and navigation
  private menuStateSubject = new BehaviorSubject<DocumentationMenuState>({
    expandedSections: [],
    breadcrumbs: []
  });

  constructor(private http: HttpClient) {}

  /**
   * Load documentation configuration from JSON file
   * Results are cached for performance
   */
  getDocumentationConfig(): Observable<DocumentationConfig> {
    if (!this.configCache$) {
      this.configCache$ = this.http.get<DocumentationConfig>(this.configUrl).pipe(
        catchError(this.handleError('Failed to load documentation configuration')),
        shareReplay(1) // Cache the result
      );
    }
    return this.configCache$;
  }

  /**
   * Get documentation content by route (resolves route to file path automatically)
   */
  getDocumentationContentByRoute(route: string): Observable<string> {
    return this.getDocumentationConfig().pipe(
      switchMap(config => {
        const item = this.findItemByRoute(config.sections, route);
        
        if (!item || !item.filePath) {
          return throwError(() => new Error(`No documentation found for route: ${route}`));
        }
        
        return this.getDocumentationContent(item.filePath);
      })
    );
  }

  /**
   * Load Markdown content for a specific documentation item
   */
  getDocumentationContent(filePath: string): Observable<string> {
    const fullUrl = `${this.baseUrl}/${filePath}`;
    
    return this.http.get(fullUrl, { responseType: 'text' }).pipe(
      catchError(error => {
        return this.handleError(`Failed to load documentation: ${filePath}`)(error);
      })
    );
  }

  /**
   * Get structured documentation content with navigation context
   */
  getDocumentationItem(route: string): Observable<DocumentationContent | null> {
    return this.getDocumentationConfig().pipe(
      map(config => {
        const item = this.findItemByRoute(config.sections, route);
        if (!item || !item.filePath) {
          return null;
        }

        return {
          id: item.id,
          title: item.title,
          content: '', // Will be loaded separately
          metadata: {
            filePath: item.filePath,
            tags: item.tags,
            crossReferences: item.crossReferences
          },
          navigation: this.buildNavigationContext(config.sections, route)
        };
      }),
      catchError(this.handleError(`Failed to get documentation item: ${route}`))
    );
  }

  /**
   * Get complete content with rendered markdown
   */
  getCompleteDocumentationContent(route: string): Observable<DocumentationContent | null> {
    return this.getDocumentationItem(route).pipe(
      map(item => {
        if (!item) return null;
        
        // Load markdown content
        this.getDocumentationContent(item.metadata.filePath).subscribe(
          content => {
            item.content = content;
          }
        );
        
        return item;
      })
    );
  }

  /**
   * Get flattened list of all documentation items
   */
  getAllDocumentationItems(): Observable<DocumentationItem[]> {
    return this.getDocumentationConfig().pipe(
      map(config => this.flattenItems(config.sections)),
      catchError(this.handleError('Failed to get all documentation items'))
    );
  }

  /**
   * Search documentation items by title or description
   */
  searchDocumentation(query: string): Observable<DocumentationItem[]> {
    return this.getAllDocumentationItems().pipe(
      map(items => 
        items.filter(item => 
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          item.description.toLowerCase().includes(query.toLowerCase())
        )
      )
    );
  }

  /**
   * Get menu state observable
   */
  getMenuState(): Observable<DocumentationMenuState> {
    return this.menuStateSubject.asObservable();
  }

  /**
   * Update menu state
   */
  updateMenuState(state: Partial<DocumentationMenuState>): void {
    const currentState = this.menuStateSubject.value;
    this.menuStateSubject.next({ ...currentState, ...state });
  }

  /**
   * Toggle section expansion state
   */
  toggleSection(sectionId: string): void {
    const currentState = this.menuStateSubject.value;
    const expandedSections = [...currentState.expandedSections];
    
    const index = expandedSections.indexOf(sectionId);
    if (index >= 0) {
      expandedSections.splice(index, 1);
    } else {
      expandedSections.push(sectionId);
    }

    this.updateMenuState({ expandedSections });
  }

  /**
   * Set active section and update breadcrumbs
   */
  setActiveSection(route: string): void {
    this.getDocumentationConfig().subscribe(config => {
      const breadcrumbs = this.buildBreadcrumbs(config.sections, route);
      this.updateMenuState({ activeSection: route, breadcrumbs });
    });
  }

  /**
   * Set active route and update menu state
   */
  setActiveRoute(route: string): void {
    // Just update the active section for now
    this.setActiveSection(route);
  }

  /**
   * Clear content cache
   */
  clearCache(): void {
    this.contentCache.clear();
    this.configCache$ = undefined;
  }

  /**
   * Build breadcrumb navigation for a route
   */
  private buildBreadcrumbs(sections: DocumentationSection[], route: string): BreadcrumbItem[] {
    const breadcrumbs: BreadcrumbItem[] = [];
    
    for (const section of sections) {
      const path = this.findPathToRoute(section, route, [section]);
      if (path) {
        return path.map((item, index) => ({
          title: item.title,
          route: item.route || item.id,
          active: index === path.length - 1
        }));
      }
    }
    
    return breadcrumbs;
  }

  /**
   * Find path to a specific route in the documentation tree
   */
  private findPathToRoute(
    section: DocumentationSection | DocumentationItem, 
    targetRoute: string, 
    currentPath: (DocumentationSection | DocumentationItem)[]
  ): (DocumentationSection | DocumentationItem)[] | null {
    const routeToCheck = section.route || section.id;
    
    if (routeToCheck === targetRoute) {
      return currentPath;
    }

    if ('items' in section && section.items) {
      for (const item of section.items) {
        const path = this.findPathToRoute(item, targetRoute, [...currentPath, item]);
        if (path) return path;
      }
    }

    return null;
  }

  /**
   * Find documentation item by route
   */
  private findItemByRoute(sections: DocumentationSection[], route: string): DocumentationItem | null {
    for (const section of sections) {
      // Check if the section itself matches the route and has content
      if (section.route === route && section.filePath) {
        return section as unknown as DocumentationItem;
      }
      
      // Check items within the section
      if (section.items) {
        const found = this.findItemInItems(section.items, route);
        if (found) {
          return found;
        }
      }
    }
    
    return null;
  }

  /**
   * Recursively find item in nested items
   */
  private findItemInItems(items: DocumentationItem[], route: string): DocumentationItem | null {
    for (const item of items) {
      if (item.route === route) {
        return item;
      }
      
      if (item.items) {
        const found = this.findItemInItems(item.items, route);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Build navigation context for a specific route
   */
  private buildNavigationContext(sections: DocumentationSection[], route: string): NavigationContext {
    const allItems = this.flattenItems(sections);
    const currentIndex = allItems.findIndex(item => item.route === route);
    
    const context: NavigationContext = {};
    
    if (currentIndex > 0) {
      const prev = allItems[currentIndex - 1];
      context.previous = {
        title: prev.title,
        route: prev.route,
        description: prev.description
      };
    }
    
    if (currentIndex < allItems.length - 1) {
      const next = allItems[currentIndex + 1];
      context.next = {
        title: next.title,
        route: next.route,
        description: next.description
      };
    }

    // Find parent and children
    const item = allItems[currentIndex];
    if (item) {
      context.parent = this.findParent(sections, item.id);
      context.children = item.items?.map(child => ({
        title: child.title,
        route: child.route,
        description: child.description
      })) || [];
    }

    return context;
  }

  /**
   * Find parent of a documentation item
   */
  private findParent(sections: DocumentationSection[], itemId: string): NavigationLink | undefined {
    for (const section of sections) {
      if (section.items?.some(item => item.id === itemId)) {
        return {
          title: section.title,
          route: section.route || section.id,
          description: section.description
        };
      }
      
      if (section.items) {
        for (const item of section.items) {
          if (item.items?.some(child => child.id === itemId)) {
            return {
              title: item.title,
              route: item.route,
              description: item.description
            };
          }
        }
      }
    }
    return undefined;
  }

  /**
   * Flatten nested documentation structure into a single array
   */
  private flattenItems(sections: DocumentationSection[]): DocumentationItem[] {
    const items: DocumentationItem[] = [];
    
    for (const section of sections) {
      if (section.filePath) {
        items.push(section as unknown as DocumentationItem);
      }
      
      if (section.items) {
        items.push(...this.flattenItemsRecursive(section.items));
      }
    }
    
    return items.sort((a, b) => a.order - b.order);
  }

  /**
   * Recursively flatten nested items
   */
  private flattenItemsRecursive(items: DocumentationItem[]): DocumentationItem[] {
    const result: DocumentationItem[] = [];
    
    for (const item of items) {
      result.push(item);
      
      if (item.items) {
        result.push(...this.flattenItemsRecursive(item.items));
      }
    }
    
    return result;
  }

  /**
   * Generic error handler for HTTP operations
   */
  private handleError(operation: string) {
    return (error: HttpErrorResponse): Observable<never> => {
      const docError: DocumentationError = {
        type: 'http-error',
        message: `${operation}: ${error.message}`,
        code: error.status?.toString(),
        filePath: error.url || undefined
      };
      
      console.error('DocumentationService Error:', docError);
      return throwError(() => docError);
    };
  }
}